import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/entries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PatientHistoryDialog from "@/components/PatientHistoryDialog";
import { Ban, Search, X } from "lucide-react";

export default function IncompleteSheet() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [historyName, setHistoryName] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sheets/incomplete"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((e) => e.patient_name.toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <div className="px-6 lg:px-10 pt-4 lg:pt-6 pb-3 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
          <Ban size={18} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Follow-ups closed as "no further follow-up needed"</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">Incomplete follow-up/treatment</h1>
        </div>
      </div>

      <Card className="mt-3 border-slate-200 p-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 pr-9" placeholder="Search patient name in this sheet..."
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="incomplete-search-input" />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="incomplete-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center">
              <X size={14} />
            </button>
          )}
        </div>
      </Card>

      <Card className="mt-3 mb-3 border-slate-200 overflow-hidden [&>div]:max-h-[calc(100vh-190px)] [&>div]:overflow-auto" data-testid="incomplete-sheet">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="incomplete-empty">
            {q ? `No matches for "${q}".` : "No follow-ups closed as \"no further follow-up needed\" yet."}
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(226,232,240,1)]">
              <TableRow>
                <TableHead className="w-14">S.No.</TableHead>
                <TableHead className="w-40">Date</TableHead>
                <TableHead className="w-56">Patient</TableHead>
                <TableHead>Closure note</TableHead>
                <TableHead className="w-32 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e, i) => (
                <TableRow key={e.id} data-testid={`incomplete-row-${e.id}`}>
                  <TableCell className="text-slate-500 text-sm w-14" data-testid={`incomplete-sno-${e.id}`}>{i + 1}</TableCell>
                  <TableCell className="text-slate-700 whitespace-nowrap" data-testid={`incomplete-date-${e.id}`}>
                    {fmtDate(e.completed_at)}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    <button type="button" onClick={() => setHistoryName(e.patient_name)}
                            data-testid={`incomplete-name-${e.id}`}
                            className="hover:text-amber-700 hover:underline text-left">
                      {e.patient_name}
                    </button>
                  </TableCell>
                  <TableCell className="text-slate-600">{e.closure?.note || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Link to={`/entries/${e.id}`}>
                      <Button size="sm" variant="outline" data-testid={`incomplete-view-${e.id}`}>View</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <PatientHistoryDialog
        open={!!historyName}
        onOpenChange={(v) => !v && setHistoryName(null)}
        patientName={historyName}
      />
    </div>
  );
}
